import React, { useState } from 'react';
import PageBar from '../../components/PageBar';
import useMediaQuery from '../../hooks/useMediaQuery';
import { DESKTOP_QUERY } from '../../components/PageBar';
import { Pill } from '../admin/adminUtils';
import { dueState, DueChip } from '../home/parts';
import {
  CYCLE_STATS, CycleStat, CycleStrip, LinesOnCard, RowButton,
  STATE_COLOUR, cardColumns, locationName,
} from './parts';
import { pressable } from '../../lib/pressable';

/*
  My Counts: the locations one person holds in the open count.

  Built to Group C's approved drawing on 5 September 2026, and it is rule 23's
  screen: **it exists for everybody and means the same thing.** An admin, a
  manager and a rep all hold locations of their own, and all three land here.

  **There were FOUR copies of it**, and this is the fourth screen in this job
  where that has been true. A desk version in `components/DesktopMyCounts.jsx`,
  a completely separate held version written inline in `RepDashboard.jsx` with
  its own card shape and its own words, and two loaders, one in
  `pages/admin/MyCounts.jsx` and one inside `ManagerDashboard.jsx`, that were
  copies of each other.

  **They had drifted, and one of the drifts was live.** The manager's loader
  built its per-location figures without `lines` and `counted`, the two the card
  needs to say how much of a sheet is filled in, so **every card on a manager's
  My Counts said "No lines on this sheet yet" whether it was true or not.**
  Nothing said anything; the card simply printed the sentence it had.

  The two versions also disagreed about the words. A desk said "Submitted for
  Review" where a phone said "Submitted", and both wrote their statuses in title
  case where the rest of the app writes them in sentence case.
*/

// What one card says, in the order the drawing says it.
function LocationCard({ c, lines, pending, onOpen, onRequestEdit, onViewOnly, profile }) {
  const locked        = c.status === 'submitted' || c.status === 'approved';
  // At a regional office somebody else holding the sheet does not make it read
  // only: everybody who covers it counts it (the owner's word, 18 September 2026).
  const heldByOther   = !locked && c.rep_id && profile?.id && c.rep_id !== profile.id
                        && !c.warehouse?.allows_concurrent_counting;
  const prefix        = c.warehouse?.region_prefix;

  /*
    **Rule 33: the act a person came to do is on the card.** Start count and
    Continue count are the whole reason this screen exists, and Group C's drawing
    gives a sheet already handed in the quiet View count, because looking at what
    you sent is the common thing to want.

    **Asking for a sheet back is the second act, so it is the second thing.**
    The app used to put it where View count is, which meant a rep could not look
    at their own submitted count from this screen at all: the only button on it
    sent a request to an administrator. It is a quiet line beside the pill now,
    and a request already in flight says so rather than offering to send another.
  */
  const action = heldByOther
    ? <RowButton tone="caution" onClick={() => onViewOnly(c)}
        title={'This count is open with ' + (c.owner?.full_name || 'another rep')}>View only</RowButton>
    : !locked
      ? <RowButton onClick={() => onOpen(c)}>{c.status === 'not_started' ? 'Start count' : 'Continue count'}</RowButton>
      : <RowButton tone="quiet" onClick={() => onOpen(c)}>View count</RowButton>;

  /*
    **"Asked to reopen", not "Reopening asked for."** Rule 30: a label that wraps
    is a fault no width check can see. The longer phrase fitted at every width
    except three cards across a 1194 tablet, where it dropped the button onto a
    second line and made that one card taller than its neighbours. Found by
    opening the picture.
  */
  const askBack = locked && !heldByOther && (pending
    ? <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Asked to reopen</span>
    : <button type="button" className="ask-back" onClick={e => { e.stopPropagation(); onRequestEdit(c); }}
        style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--action-text)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
        Ask to reopen
      </button>);

  const canOpen = heldByOther || !locked;
  return (
    <div
      {...pressable(canOpen ? () => (heldByOther ? onViewOnly(c) : onOpen(c)) : undefined)}
      style={{
        background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: 'var(--oh-radius-card)',
        boxShadow: 'var(--depth-rest)', display: 'flex', overflow: 'hidden',
        cursor: canOpen ? 'pointer' : 'default',
      }}>
      {/* Rule 11: the state bar is a solid block down the left edge, inset. */}
      <span style={{ width: 4, flexShrink: 0, background: STATE_COLOUR[c.status] || 'var(--text-muted)' }} />
      <div style={{ flex: 1, minWidth: 0, padding: '15px 18px 13px', display: 'flex', flexDirection: 'column' }}>
        {/* **The territory above the name, and the clean name.** Rule 16. A
            location is stored as "DFW-ASPEN GROVE ORTHOPEDICS" and printing that
            raw says DFW twice once the prefix is on its own line. */}
        {prefix && (
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.4, color: 'var(--text-secondary)' }}>{prefix}</div>
        )}
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.3 }}>
          {c.warehouse?.display_name || c.warehouse?.name}
        </div>
        <div style={{ marginTop: 10 }}>
          <LinesOnCard n={lines} />
        </div>
        <div style={{
          marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--border-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <Pill status={c.status} />
            {askBack}
          </span>
          {action}
        </div>
      </div>
    </div>
  );
}

export default function MyCountsView({
  cycle, counts, stats, countValueMap, pendingRequests, profile,
  onOpen, onRequestEdit, onViewOnly, onAddLocation,
}) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const isTwoUp   = useMediaQuery('(min-width: 768px)');
  /*
    **Two filters, from the drawing**: a search line and a status picker. A rep
    with 40 locations was scrolling to find one, and the one thing they most
    often want, "the ones I have not done", had no control at all.
  */
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  if (!cycle) {
    return (
      <div className="card" style={{ maxWidth: 480, padding: 32, textAlign: 'center' }}>
        <div className="oh-title" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>No open count</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>There is no count open at the moment. An administrator opens the next one.</div>
      </div>
    );
  }

  const total = counts.length;
  const pct   = total > 0 ? Math.round((stats.submitted + stats.approved) / total * 100) : 0;
  /*
    **"Still to count" is the number a rep opens this screen for**, which is why
    the drawing puts it first and in the count's own colour. It is the sheets not
    started plus the sheets part way through: everything with work left on it.
  */
  const stillToCount = stats.not_started + stats.in_progress;

  const text = search.trim().toLowerCase();
  const shown = counts
    .filter(c => statusFilter === 'all' || c.status === statusFilter)
    .filter(c => !text || ((c.warehouse?.name || '') + ' ' + (c.warehouse?.display_name || '')).toLowerCase().includes(text))
    // **Alphabetical, on the name a person reads.** They came out ordered by
    // status, which puts a location in a different place on the screen every
    // time somebody counts something.
    .slice()
    .sort((a, b) => locationName(a).localeCompare(locationName(b)));

  return (
    <>
      {/*
        The cycle strip, in place of a card 190 pixels tall carrying a completion
        ring and three figures headed Items, Units and Est. Value. Rule 5.

        **Items, Units and Est. Value are gone on purpose.** They were the whole
        cycle's totals shown on a screen about one person's locations, and the
        one a rep might have wanted, how much is left, was not among them. Each
        card carries its own now, which is what the drawing shows.
      */}
      <PageBar
        twoRows
        metaFullWidth={!isTwoUp}
        /* The overdue label. See the note on the same line in AdminDashboard:
           the guide's decision 2 replaces red unstarted counts with a red label
           on a late one, and a rep needs it here more than anywhere. */
        title={<>{cycle.name} <span className="pill pill-app" style={{ marginLeft: 8 }}>Cycle open</span>
          {dueState(cycle.due_on)?.tone === 'late' && (
            <span style={{ marginLeft: 8 }}><DueChip due={dueState(cycle.due_on)} /></span>
          )}</>}
        subtitle={isTwoUp
          ? total + ' location' + (total !== 1 ? 's' : '') + ' assigned to you'
          : pct + '% done - ' + stillToCount + ' of ' + total + ' left'}
        meta={isTwoUp ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <CycleStat n={stillToCount} label="Still to count" dot="var(--text-muted)" />
            <CycleStat n={pct + '%'} label="Complete" dot="var(--info)" />
            {CYCLE_STATS.map(s => (
              <CycleStat key={s.key} n={stats[s.key]} label={s.label} dot={s.dot}
                on={statusFilter === s.key}
                onClick={() => setStatusFilter(f => f === s.key ? 'all' : s.key)} />
            ))}
          </span>
        ) : (
          <CycleStrip stats={stats} filter={statusFilter} onFilter={setStatusFilter} />
        )}
        controls={
          <>
            <input className="tb-search" placeholder="Search your locations"
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className={'tb-select' + (statusFilter !== 'all' ? ' set' : '')}
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
            </select>
          </>
        }
        actions={<button className="btn btn-outline" onClick={onAddLocation}>Add a location</button>}
      />

      {/* **The heading says what is left, not just what the section is.** The
          drawing's own words, and it is the one number this screen is opened
          for. */}
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 10 }}>
        <span className="oh-ground-tag">
          Your locations
          <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
            {' - ' + stillToCount + ' still to count'}
          </span>
        </span>
      </div>

      {shown.length === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          {total === 0
            ? 'You have no locations in this count yet.'
            : 'No locations match the current filter.'}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(' + cardColumns(isDesktop, isTwoUp) + ', minmax(0, 1fr))',
          gap: 14,
        }}>
          {shown.map(c => (
            <LocationCard
              key={c.id}
              c={c}
              lines={countValueMap?.[c.id]}
              pending={pendingRequests?.[c.id]}
              profile={profile}
              onOpen={onOpen}
              onRequestEdit={onRequestEdit}
              onViewOnly={onViewOnly}
            />
          ))}
        </div>
      )}

      {statusFilter !== 'all' && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn btn-outline" onClick={() => setStatusFilter('all')}>Clear the filter, show all</button>
        </div>
      )}
    </>
  );
}
